// Typed wrappers for every backend endpoint the app talks to.
//
// Each function returns the apiRequest result: { ok, status, data }.
import { apiRequest } from './client'
import type {
  ActiveInstance,
  CaptureItem,
  FolderListItem,
  LoginCheckResponse,
  LoginResponse,
  MediaFolderUsage,
  PlaybackSettings,
  PlayRequest,
  PlaylistChannel,
  PlaylistFilterSettings,
  Preset,
  RemoteActionType,
  ScreenShareConfig,
  ScreenShareSession,
  StartInstanceRequest,
  VideoTokenResponse,
} from '@expostreamer/shared-types'

// --- Auth ---
export const login = (username?: string, password?: string) =>
  apiRequest<LoginResponse>('/api/login', {
    method: 'POST',
    auth: false,
    body: { username, password },
  })

export const checkLogin = () => apiRequest<LoginCheckResponse>('/api/login/check')

// --- Instances ---
export const getActiveInstances = () => apiRequest<ActiveInstance[]>('/admin/instance/active')

export const getInstancePresets = () => apiRequest<Preset[]>('/admin/instance/presets')

export const startInstance = (body: StartInstanceRequest) =>
  apiRequest<ActiveInstance[]>('/admin/instance/start', { method: 'POST', body })

export const stopInstance = (instanceName: string) =>
  apiRequest<ActiveInstance[]>('/admin/instance/stop', { method: 'POST', body: { instanceName } })

export const editInstance = (preset: Record<string, unknown>) =>
  apiRequest('/admin/instance/edit', { method: 'POST', body: preset })

export const deletePreset = (name: string) =>
  apiRequest<Preset[]>('/admin/instance/preset', { method: 'DELETE', body: { name } })

export const play = (body: PlayRequest) =>
  apiRequest<ActiveInstance[]>('/admin/play', { method: 'POST', body })

export const getPlaybackSettings = () => apiRequest<PlaybackSettings>('/admin/settings')

export const updatePlaybackSettings = (settings: Partial<PlaybackSettings>) =>
  apiRequest<PlaybackSettings>('/admin/settings', { method: 'POST', body: settings })

// --- Capture queue ---
export const getCaptureList = () => apiRequest<CaptureItem[]>('/admin/capturelist')

export const addCaptureItem = (item: CaptureItem) =>
  apiRequest<CaptureItem[]>('/admin/capturelist/item', { method: 'POST', body: { item } })

export const deleteCaptureItem = (index: number, instanceName: string) =>
  apiRequest<CaptureItem[]>('/admin/capturelist/item', {
    method: 'DELETE',
    body: { index, instanceName },
  })

// --- Media / records ---
export const getFolderList = () => apiRequest<MediaFolderUsage[]>('/admin/folder/list')

export const listRecords = (path?: string) =>
  apiRequest<FolderListItem[]>('/records/list', { method: 'POST', body: { path } })

export const getVideoToken = (drive?: string, folder?: string) =>
  apiRequest<VideoTokenResponse>(
    '/records/video/token' +
      (drive
        ? '?drive=' + encodeURIComponent(drive) + '&folder=' + encodeURIComponent(folder ?? '')
        : '')
  )

// `query` is the part after '?' (e.g. "name=foo.mp4&drive=Movies&folder=sub/").
export const deleteRecord = (query: string) =>
  apiRequest('/records/video?' + query, { method: 'DELETE' })

// --- Channels ---
// The server returns at most CHANNEL_PAGE_SIZE results per request; pass
// `offset` to fetch the following pages.
export const CHANNEL_PAGE_SIZE = 50

export const searchChannels = (query: string, groups?: string[], offset = 0) =>
  apiRequest<PlaylistChannel[]>(
    '/playlist?searchQuery=' +
      encodeURIComponent(query) +
      (groups?.length ? '&groups=' + encodeURIComponent(groups.join(',')) : '') +
      (offset ? '&offset=' + offset : '')
  )

export const getChannelGroups = () => apiRequest<string[]>('/playlist/group/list')

export const getPlaylistFilter = () => apiRequest<PlaylistFilterSettings>('/playlist/filter')

export const savePlaylistFilter = (selectedGroups: string[]) =>
  apiRequest<PlaylistFilterSettings>('/playlist/filter', {
    method: 'POST',
    body: { selectedGroups },
  })

export const exportPlaylist = (selectedGroups: string[], useTunnel?: boolean) =>
  apiRequest<string>('/playlist/export.m3u', {
    method: 'POST',
    body: { selectedGroups, useTunnel },
  })

// --- Live ---
export const getHlsToken = () => apiRequest<{ token: string }>('/live/token')

// --- Remote ---
export const remoteAction = (action: RemoteActionType) =>
  apiRequest('/remote/action', { method: 'POST', body: { action } })

// --- Screen share ---
export const getActiveScreenShares = () => apiRequest<ScreenShareSession[]>('/screenshare/active')

export const getScreenShareConfig = () => apiRequest<ScreenShareConfig>('/screenshare/config')

export const startScreenShare = (clientId: string) =>
  apiRequest<ScreenShareSession>('/screenshare/start', { method: 'POST', body: { clientId } })

export const stopScreenShare = (shareId: string) =>
  apiRequest<ScreenShareSession[]>('/screenshare/stop', { method: 'POST', body: { shareId } })
